import { VisuallyHidden } from "./VisuallyHidden";
import { trackedDealUrl } from "../lib/click";
import styles from "./LedgerRow.module.css";
import type { ClickSurface } from "../lib/click";
import type { PublicDeal } from "../api/types";

/**
 * A single line in a ledger-style list: rank, who, and one number.
 *
 * The auction standings and the weekly/monthly rankings are both "a position, a deal and a
 * figure", but they are decided by completely different things — a bid in one case, buyer
 * clicks in the other (canon § 11, § 14). They share this row's *shape* only; each list
 * supplies its own figure label, so a reader is always told what the number is rather than
 * being left to assume it is money.
 *
 * A row is a list item and must be rendered inside an ordered list, which is what carries
 * the ranking to assistive technology.
 */
export function LedgerRow({
  rank,
  deal,
  figure,
  figureLabel,
  surface,
  muted = false,
  share,
  rankTone = "placement",
}: {
  rank: number;
  deal: PublicDeal;
  /** The already-formatted number, e.g. "$15" or "42 clicks". */
  figure: string;
  /** What that number is, e.g. "current bid". Always rendered, never implied. */
  figureLabel: string;
  /**
   * Where this row is being rendered, for core's click tracking (canon § 11).
   *
   * Required rather than defaulted: this row is shared by lists decided in completely
   * different ways, and a silent default would attribute one list's clicks to another.
   */
  surface: ClickSurface;
  /** Dimmed styling for a position that is not currently winning anything. */
  muted?: boolean | undefined;
  /**
   * This row's figure as a fraction (0–1) of the largest in its list, drawn as a bar
   * (`WEB-007`, the design canvas). Purely a second encoding of the number already printed
   * beside it: the bar is `aria-hidden`, so nothing is conveyed by length alone.
   */
  share?: number | undefined;
  /**
   * What decided this position. `placement` is bid-driven and wears brass, the colour this
   * site reserves for rank marks on the sponsored board. `earned` is click-driven and is
   * deliberately plain — canon § 11 and § 14 keep the two kinds of list apart, and a
   * weekly list wearing the board's rank colour would blur exactly that line.
   */
  rankTone?: "placement" | "earned" | undefined;
}) {
  // The product name is the row's call to action, so it goes through core's tracked
  // endpoint like every other public route to a seller destination. `trackedDealUrl` still
  // withholds the link entirely when the stored destination is not a safe http/https URL.
  const href = trackedDealUrl(deal.id, surface, deal.dealUrl);

  return (
    <li
      className={[styles.row, muted ? styles.muted : null, styles[rankTone]]
        .filter(Boolean)
        .join(" ")}
    >
      <span className={`${styles.rank} figure`} aria-hidden="true">
        {String(rank).padStart(2, "0")}
      </span>
      <span className={styles.deal}>
        <span className={styles.product}>
          <VisuallyHidden>Position {String(rank)}: </VisuallyHidden>
          {href === null ? (
            deal.productName
          ) : (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {deal.productName}
              <VisuallyHidden> (opens in a new tab)</VisuallyHidden>
            </a>
          )}
        </span>
        <span className={styles.seller}>{deal.seller.displayName}</span>
      </span>
      {share === undefined ? null : (
        <span className={styles.track} aria-hidden="true">
          <span
            className={styles.bar}
            style={{ inlineSize: `${String(Math.max(0, Math.min(1, share)) * 100)}%` }}
          />
        </span>
      )}

      <span className={styles.figure}>
        <span className={`${styles.amount} figure`}>{figure}</span>
        <span className={styles.figureLabel}>{figureLabel}</span>
      </span>
    </li>
  );
}
